import {observable, computed, action, makeObservable, runInAction} from 'mobx'

interface AggregateableState {
    isDirty: boolean
    canEdit?: boolean
    save: () => Promise<void> | void
    discard: () => Promise<void> | void
}

/**
 * @template TConstructor Constructor type of T. If you want to pass constructor parameters to getState, use "AggregateState<ClassName, typeof ClassName>".
 */
export abstract class AggregateState<
    T extends AggregateableState,
    TConstructor extends {new (...args: any): T} = {new (): T}
> {
    @observable protected states: Map<string, T> = new Map()

    public constructor() {
        makeObservable(this)
    }

    @computed
    public get isDirty(): boolean {
        return Array.from(this.states.values()).some(state => state.isDirty)
    }

    public getState(key: string | undefined, ...ctorProps: ConstructorParameters<TConstructor>): T | undefined {
        if (!key) return undefined

        let state = this.states.get(key)
        if (!state) {
            state = this.getNewState(...ctorProps)
            runInAction(() => this.states.set(key, state as T))
        }
        return state
    }

    protected abstract getNewState(...props: ConstructorParameters<TConstructor>): T

    @action.bound
    public async save(): Promise<void> {
        await Promise.all(
            Array.from(this.states.values())
                .filter(state => state.isDirty)
                .map(async state => {
                    try {
                        await state.save()
                    } catch (exc) {
                        await this.onSaveError(state, exc as Error)
                        throw exc
                    }
                })
        )
    }

    protected async onSaveError(_state: T, _exception: Error): Promise<void> {}

    @action.bound
    public async discard(): Promise<void> {
        await Promise.all(Array.from(this.states.values()).map(state => state.discard()))
    }
}
